// Authentication context provider

import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AuthContextType, AuthSession, LoginCredentials, RegistrationData } from '@/types/auth';
import { User } from '@/types/user';
import authService from '@services/authService';

// Auth state
interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  session: AuthSession | null;
  error: string | null;
}

// Auth actions
type AuthAction = 
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_SESSION'; payload: AuthSession }
  | { type: 'CLEAR_SESSION' }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' };

// Initial state
const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  session: null,
  error: null
};

// Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  console.log('AuthReducer: Action dispatched:', action.type, action);
  
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_SESSION':
      const newState = {
        ...state,
        isAuthenticated: true,
        isLoading: false,
        user: action.payload.user,
        session: action.payload,
        error: null
      };
      console.log('AuthReducer: SET_SESSION new state:', newState);
      return newState;
    
    case 'CLEAR_SESSION':
      return {
        ...state,
        isAuthenticated: false,
        isLoading: false,
        user: null,
        session: null,
        error: null
      };
    
    case 'SET_ERROR':
      return {
        ...state,
        isLoading: false,
        error: action.payload
      };
    
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    
    default:
      return state;
  }
}

// Create context
const AuthContext = createContext<AuthContextType | null>(null);

// Provider component
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = () => {
      console.log('AuthContext: Initializing auth state...');
      const session = authService.getCurrentSession();
      console.log('AuthContext: Current session from service:', session);
      
      if (session && authService.isSessionValid(session)) {
        console.log('AuthContext: Valid session found, setting session');
        dispatch({ type: 'SET_SESSION', payload: session });
      } else {
        console.log('AuthContext: No valid session, setting loading to false');
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initializeAuth();
  }, []);

  // Auth methods
  const login = async (credentials: LoginCredentials): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });
      
      console.log('AuthContext: Attempting login with:', credentials.email);
      const session = await authService.login(credentials);
      console.log('AuthContext: Login successful, session:', session);
      dispatch({ type: 'SET_SESSION', payload: session });
      console.log('AuthContext: Session dispatched successfully');
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      const message = error instanceof Error ? error.message : 'Login failed';
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authService.logout();
      dispatch({ type: 'CLEAR_SESSION' });
    } catch (error) {
      console.error('Logout error:', error);
      // Force clear session even if logout fails
      dispatch({ type: 'CLEAR_SESSION' });
    }
  };

  const register = async (data: RegistrationData): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });
      
      await authService.register(data);
      
      // After successful registration, user needs to verify email
      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Registration failed';
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const resetPassword = async (email: string): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });
      
      // Mock password reset
      console.log(`Password reset email sent to: ${email}`);
      
      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Password reset failed';
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const verifyEmail = async (token: string): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });
      
      // Mock email verification
      console.log(`Email verified with token: ${token}`);
      
      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Email verification failed';
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const refreshSession = async (): Promise<void> => {
    try {
      const newSession = await authService.refreshToken();
      dispatch({ type: 'SET_SESSION', payload: newSession });
    } catch (error) {
      console.error('Session refresh failed:', error);
      dispatch({ type: 'CLEAR_SESSION' });
    }
  };

  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value: AuthContextType = {
    // State
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    user: state.user,
    session: state.session,
    error: state.error,

    // Actions
    login,
    logout,
    register,
    resetPassword,
    verifyEmail,
    refreshSession,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook to use auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}